import React, { useEffect, useMemo, useState } from 'react';
import { Card } from '../components/ui/card';
import api from '../utils/api';
import { Calendar, Users, TrendingUp, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

const HOURS = Array.from({ length: 24 }, (_, i) => `${String(i).padStart(2, '0')}:00`);

const ReportsPage = () => {
  const [events, setEvents] = useState([]);
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get('/events'), api.get('/guests')])
      .then(([eventsRes, guestsRes]) => {
        setEvents(eventsRes.data || []);
        setGuests(guestsRes.data || []);
      })
      .finally(() => setLoading(false));
  }, []);

  const completedEvents = useMemo(() => events.filter((e) => e.status === 'completed'), [events]);

  const totalGuests = events.reduce((sum, e) => sum + (e.totalGuests || 0), 0);
  const totalAttendance = events.reduce((sum, e) => sum + (e.attendedGuests || 0), 0);
  const totalRegistered = events.reduce((sum, e) => sum + (e.registeredGuests || 0), 0);
  const attendanceRate = totalRegistered > 0 ? ((totalAttendance / totalRegistered) * 100).toFixed(1) : 0;
  const avgGuestsPerEvent = events.length > 0 ? Math.round(totalGuests / events.length) : 0;

  const monthlyEvents = useMemo(
    () =>
      Object.values(
        events.reduce((acc, e) => {
          const month = e.date ? new Date(e.date).toLocaleString('en-US', { month: 'short' }) : 'N/A';
          acc[month] = acc[month] || { month, events: 0 };
          acc[month].events += 1;
          return acc;
        }, {})
      ),
    [events]
  );

  const crowdDensity = useMemo(() => {
    const buckets = Object.fromEntries(HOURS.map((h) => [h, 0]));
    guests.forEach((g) => {
      if (!g.attended || !g.attendedAt) return;
      const hour = new Date(g.attendedAt).getHours();
      const key = `${String(hour).padStart(2, '0')}:00`;
      buckets[key] = (buckets[key] || 0) + 1;
    });
    return HOURS.map((time) => ({ time, count: buckets[time] })).filter((_, i) => i >= 6 && i <= 22);
  }, [guests]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Reports & Analytics</h1>
          <p className="text-gray-600 mt-1">Insights and statistics for your events</p>
        </div>
        <Button variant="outline" onClick={() => window.print()}>
          <Download className="w-4 h-4 mr-2" />
          Export Report
        </Button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Events</p>
              <p className="text-3xl font-bold mt-1">{events.length}</p>
            </div>
            <Calendar className="w-10 h-10 text-blue-500" />
          </div>
        </Card>
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Guests</p>
              <p className="text-3xl font-bold mt-1">{totalGuests}</p>
            </div>
            <Users className="w-10 h-10 text-green-500" />
          </div>
        </Card>
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Attendance Rate</p>
              <p className="text-3xl font-bold mt-1">{attendanceRate}%</p>
            </div>
            <TrendingUp className="w-10 h-10 text-orange-500" />
          </div>
        </Card>
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Avg. Guests/Event</p>
              <p className="text-3xl font-bold mt-1">{avgGuestsPerEvent}</p>
            </div>
            <Users className="w-10 h-10 text-purple-500" />
          </div>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Monthly Events Trend */}
        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4">Monthly Events Trend</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={monthlyEvents}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Bar dataKey="events" fill="#f97316" name="Events" />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* Crowd Density */}
        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4">Check-in Activity (Hourly)</h2>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={crowdDensity}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="time" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="count" stroke="#f97316" strokeWidth={2} name="Check-ins" />
            </LineChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Event Performance Table */}
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4">Event Performance</h2>
        {completedEvents.length === 0 ? (
          <p className="text-sm text-gray-500">No completed events yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4">Event Name</th>
                  <th className="text-left py-3 px-4">Date</th>
                  <th className="text-left py-3 px-4">Registered</th>
                  <th className="text-left py-3 px-4">Attended</th>
                  <th className="text-left py-3 px-4">Rate</th>
                </tr>
              </thead>
              <tbody>
                {completedEvents.map((event) => {
                  const rate = event.registeredGuests
                    ? ((event.attendedGuests / event.registeredGuests) * 100).toFixed(1)
                    : '0.0';
                  return (
                    <tr key={event.id} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4 font-medium">{event.name}</td>
                      <td className="py-3 px-4 text-gray-600">
                        {new Date(event.date).toLocaleDateString()}
                      </td>
                      <td className="py-3 px-4">{event.registeredGuests}</td>
                      <td className="py-3 px-4">{event.attendedGuests}</td>
                      <td className="py-3 px-4">
                        <span
                          className={`px-2 py-1 rounded text-xs font-semibold ${
                            rate >= 80
                              ? 'bg-green-100 text-green-700'
                              : rate >= 60
                              ? 'bg-orange-100 text-orange-700'
                              : 'bg-red-100 text-red-700'
                          }`}
                        >
                          {rate}%
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

export default ReportsPage;
